import { Icon } from '@iconify/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../Shared/Accordion/Accordion';
import { FormDescription } from '../../Shared/FormCustom/FormCustom';
import { Input } from '../../Shared/Input/Input';
import { AutosizeTextarea } from '../../Shared/Textarea/Textarea';
import { SheetConfirmationCloseDialog } from '../SheetConfirmationCloseDialog';

import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/Domain/components/Shared/Form/Form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { useKnowledgeBaseActionForm } from '@/Domain/hooks/Action/KnowledgeBaseAction/useKnowledgeBaseActionForm.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

type KnowledgeBaseActionDialogProps = {
  knowledgeBaseTags: string[];
};

export const KnowledgeBaseActionSheet = ({ knowledgeBaseTags }: KnowledgeBaseActionDialogProps) => {
  const { t } = useTranslation('knowledge_base_action');
  const { isOpen, closeModal, params } = useModalStore();
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const scrollContainerRef = useRef<HTMLElement>(null);

  const { knowledgeBaseAction } = params || {};
  const { form, handleSubmit, onInvalid } = useKnowledgeBaseActionForm(knowledgeBaseAction);

  const handleNeedHelp = () => window.open('https://docs.quickchat.ai/', '_blank');
  const { isDirty } = useFormState({ control: form.control });
  const handleClose = () => {
    if (isDirty) {
      setConfirmationModalOpen(true);
    } else {
      closeModal();
      form.reset();
    }
  };
  const handleConfirmClose = () => {
    setConfirmationModalOpen(false);
    closeModal();
    form.reset();
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    },
    [handleClose],
  );

  useEffect(() => {
    if (!isOpen(MODAL_NAMES.KNOWLEDGE_BASE_ACTION_SHEET)) return;

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen(MODAL_NAMES.KNOWLEDGE_BASE_ACTION_SHEET), handleKeyDown]);

  useEffect(() => {
    if (!knowledgeBaseAction) return;
  }, [knowledgeBaseAction]);

  return (
    <div data-testid="knowledge-base-action-dialog">
      <Form {...form}>
        <Sheet open={isOpen(MODAL_NAMES.KNOWLEDGE_BASE_ACTION_SHEET)} onOpenChange={closeModal}>
          <SheetContent
            aria-describedby={undefined}
            className="!max-w-[100vw] xl:!max-w-[85%] 2xl:!max-w-7xl w-full gap-0"
            tabIndex={undefined}
            asChild
          >
            <form className="h-full">
              <SheetHeader className="pb-4 border-b border-b-border">
                <SheetTitle>{t('sheet.title')}</SheetTitle>
                <div className="flex items-center gap-2 pr-1">
                  <Button variant="link" onClick={handleNeedHelp} className="!pl-0">
                    <Icon
                      icon="mingcute:question-line"
                      className="size-5 text-muted-foreground  "
                    />
                    <span className="!text-muted-foreground text-sm">{t('button.need_help')}</span>
                  </Button>
                  <Button
                    onClick={form.handleSubmit(handleSubmit, onInvalid)}
                    data-testid="knowledge-base-action-sheet-submit-button"
                  >
                    {t('button.save')}
                  </Button>
                  <IconButton variant="secondary" onClick={handleClose}>
                    <Icon icon="mingcute:close-line" />
                  </IconButton>
                </div>
              </SheetHeader>
              <div className="grid grid-cols-1 h-full overflow-hidden">
                <section
                  ref={scrollContainerRef}
                  className="space-y-7 relative px-1 h-full custom-scroll overflow-y-auto scroll-stable"
                >
                  <FormField
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('sheet.name.label')}</FormLabel>
                        <FormDescription>{t('sheet.name.description')}</FormDescription>
                        <FormControl>
                          <Input
                            data-testid="knowledge-base-action-form-name-input"
                            placeholder={t('sheet.name.placeholder')}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    name="tag"
                    render={({ field }) => (
                      <FormItem className="py-1">
                        <FormLabel> {t('sheet.tag.label')}</FormLabel>
                        <FormDescription> {t('sheet.tag.description')} </FormDescription>
                        <FormControl>
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger
                              data-testid="knowledge-base-action-form-tag-input"
                              className="w-full"
                              size="md"
                              ref={field.ref}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {knowledgeBaseTags.map((value) => (
                                <SelectItem key={value} value={value}>
                                  <div data-testid="knowledge-base-action-form-tag-select-option">
                                    {value}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('sheet.description_ai.label')}</FormLabel>
                        <FormDescription>{t('sheet.description_ai.description')}</FormDescription>
                        <FormControl>
                          <AutosizeTextarea
                            data-testid="knowledge-base-action-form-description-input"
                            placeholder={t('sheet.description_ai.placeholder')}
                            minHeight={100}
                            className="px-4 mb-1"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Accordion
                    type="single"
                    collapsible
                    className="w-full"
                    data-testid="knowledge-base-action-sheet-advanced-options-accordion"
                  >
                    <AccordionItem
                      value="advanced_options"
                      className="rounded-xl border border-border py-2 px-6"
                    >
                      <AccordionTrigger className="text-foreground font-medium px-1">
                        <div className="flex items-center gap-3">
                          <Icon icon="mingcute:settings-2-line" /> {t('sheet.advanced_options')}
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="border-t pt-4 flex flex-col gap-10 px-1">
                        <FormField
                          name="parameter_description"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('sheet.query_description_ai.label')}</FormLabel>
                              <FormDescription>
                                {t('sheet.query_description_ai.description')}
                              </FormDescription>
                              <FormControl>
                                <AutosizeTextarea
                                  data-testid="knowledge-base-action-form-parameter-description-input"
                                  placeholder={t('sheet.query_description_ai.placeholder')}
                                  minHeight={100}
                                  className="px-4 mb-1"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </section>
              </div>
              <SheetConfirmationCloseDialog
                confirmationModalOpen={confirmationModalOpen}
                setConfirmationModalOpen={setConfirmationModalOpen}
                handleConfirmClose={handleConfirmClose}
              />
            </form>
          </SheetContent>
        </Sheet>
      </Form>
    </div>
  );
};
